/**
 * Channel Color Utilities
 * 
 * Provides utilities for channel colors with accessibility support:
 * - Single source of truth via CSS tokens
 * - Automatic text color selection based on background luminance
 * - Consistent hover/selected state styling
 */

/**
 * Calculate relative luminance of a color (WCAG formula)
 * @param {string} hex - Hex color code (e.g., "#4A6FAE")
 * @returns {number} Luminance value between 0 and 1
 */
function getLuminance(hex) {
  // Remove # if present
  const cleanHex = hex.replace('#', '');
  
  // Parse RGB components
  const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255;
  
  // Apply gamma correction
  const rsRGB = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
  const gsRGB = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
  const bsRGB = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);
  
  // Calculate relative luminance
  return 0.2126 * rsRGB + 0.7152 * gsRGB + 0.0722 * bsRGB;
}

/**
 * Get appropriate text color (white or dark) based on background luminance
 * @param {string} backgroundColorHex - Background color hex code
 * @returns {string} Text color hex code (#FFFFFF for dark backgrounds, #111827 for light)
 */
export function getTextColorForBackground(backgroundColorHex) {
  if (!backgroundColorHex) {
    return '#111827'; // Default to dark text
  }
  
  // Handle CSS variables - if it's a var(), we can't compute luminance
  // In that case, we'll need to get the computed value
  if (backgroundColorHex.startsWith('var(')) {
    // For CSS variables, we'll use a fallback approach
    // Try to get computed value from document if available
    if (typeof window !== 'undefined' && window.getComputedStyle) {
      const testEl = document.createElement('div');
      testEl.style.backgroundColor = backgroundColorHex;
      document.body.appendChild(testEl);
      const computed = window.getComputedStyle(testEl).backgroundColor;
      document.body.removeChild(testEl);
      
      // Parse rgb/rgba to hex
      const rgbMatch = computed.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
      if (rgbMatch) {
        const r = parseInt(rgbMatch[1], 10);
        const g = parseInt(rgbMatch[2], 10);
        const b = parseInt(rgbMatch[3], 10);
        backgroundColorHex = `#${[r, g, b].map(x => {
          const hex = x.toString(16);
          return hex.length === 1 ? '0' + hex : hex;
        }).join('')}`;
      } else {
        return '#111827'; // Fallback
      }
    } else {
      return '#111827'; // Fallback for SSR
    }
  }
  
  const luminance = getLuminance(backgroundColorHex);
  
  // Use white text for backgrounds with luminance < 0.5 (darker backgrounds)
  // Use dark text for backgrounds with luminance >= 0.5 (lighter backgrounds)
  return luminance < 0.5 ? '#FFFFFF' : '#111827';
}

/**
 * Get text color and font-weight based on background contrast (Option 3)
 * Returns both text color and font-weight based on the same luminance calculation:
 * - White text (dark backgrounds): font-weight 500 (medium) for readability
 * - Dark text (light backgrounds): font-weight 400 (regular) to avoid bold appearance
 * 
 * This is contrast-based, NOT channel-based. The decision is made solely on the
 * background luminance, ensuring consistent styling across all channels.
 * 
 * @param {string} backgroundColorHex - Background color hex code or CSS variable
 * @returns {Object} Object with textColor and fontWeight properties
 */
export function getTextColorAndWeightForBackground(backgroundColorHex) {
  if (!backgroundColorHex) {
    return {
      textColor: '#111827',
      fontWeight: 'var(--font-weight-normal)', // 400 for dark text
    };
  }
  
  // Handle CSS variables - resolve to hex for luminance calculation
  let resolvedHex = backgroundColorHex;
  if (backgroundColorHex.startsWith('var(')) {
    if (typeof window !== 'undefined' && window.getComputedStyle) {
      const testEl = document.createElement('div');
      testEl.style.backgroundColor = backgroundColorHex;
      document.body.appendChild(testEl);
      const computed = window.getComputedStyle(testEl).backgroundColor;
      document.body.removeChild(testEl);
      
      // Parse rgb/rgba to hex
      const rgbMatch = computed.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
      if (rgbMatch) {
        const r = parseInt(rgbMatch[1], 10);
        const g = parseInt(rgbMatch[2], 10);
        const b = parseInt(rgbMatch[3], 10);
        resolvedHex = `#${[r, g, b].map(x => {
          const hex = x.toString(16);
          return hex.length === 1 ? '0' + hex : hex;
        }).join('')}`;
      } else {
        // Fallback: assume light background (dark text, regular weight)
        return {
          textColor: '#111827',
          fontWeight: 'var(--font-weight-normal)', // 400 for dark text
        };
      }
    } else {
      // SSR fallback: assume light background (dark text, regular weight)
      return {
        textColor: '#111827',
        fontWeight: 'var(--font-weight-normal)', // 400 for dark text
      };
    }
  }
  
  const luminance = getLuminance(resolvedHex);
  const isDarkBackground = luminance < 0.5;
  
  // Contrast-based decision (not channel-based):
  // - Dark background (luminance < 0.5) -> white text + medium weight (500) for readability
  // - Light background (luminance >= 0.5) -> dark text + regular weight (400) to avoid bold appearance
  return {
    textColor: isDarkBackground ? '#FFFFFF' : '#111827',
    fontWeight: isDarkBackground 
      ? 'var(--font-weight-medium)' // 500 for white text on dark backgrounds
      : 'var(--font-weight-normal)', // 400 for dark text on light backgrounds
  };
}

/**
 * Get channel color from CSS token (strict resolver - single source of truth)
 * This is the shared resolver used by BOTH legend and pills
 * STRICT: Only accepts canonical master channel keys. Unknown values use visible fallback.
 * @param {string} channelKey - Canonical master channel key: 'vrbo', 'airbnb', 'clc', 'owner'
 * @returns {string} CSS variable reference (e.g., "var(--color-channel-vrbo)")
 */
export function getChannelColorToken(channelKey) {
  const normalized = (channelKey || '').toLowerCase().trim();
  
  // STRICT mapping - only canonical master channel keys
  const tokenMap = {
    'vrbo': 'var(--color-channel-vrbo)',
    'airbnb': 'var(--color-channel-airbnb)',
    'clc': 'var(--color-channel-clc)',
    'owner': 'var(--color-channel-owner)',
  };
  
  // STRICT: No silent fallbacks. Unknown values use visible fallback and log warning.
  if (tokenMap[normalized]) {
    return tokenMap[normalized];
  }
  
  // Unknown masterChannel - use visible fallback (neutral gray) and warn in dev
  if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
    console.warn('[getChannelColorToken] Unknown master channel key:', channelKey, '- using fallback color');
  }
  
  // Visible fallback: neutral gray (not a channel color to prevent misclassification)
  return 'var(--color-text-tertiary, #737373)';
}

/**
 * Get channel color for a master channel object (shared resolver for legend and pills)
 * Resolves master channel key and returns CSS variable
 * @param {Object|string} masterChannelOrKey - Master channel object with master_channel_key, or channel key string
 * @returns {string} CSS variable reference (e.g., "var(--color-channel-vrbo)")
 */
export function getChannelColorVar(masterChannelOrKey) {
  // Handle both object and string inputs
  const channelKey = typeof masterChannelOrKey === 'object' && masterChannelOrKey?.master_channel_key
    ? masterChannelOrKey.master_channel_key
    : typeof masterChannelOrKey === 'string'
    ? masterChannelOrKey
    : null;
  
  return getChannelColorToken(channelKey);
}

/**
 * Get channel color hex value (for cases where hex is needed)
 * This should only be used when CSS variables aren't available
 * @param {string} channelKey - Channel key: 'vrbo', 'airbnb', 'clc', 'owner'
 * @returns {string} Hex color code
 */
export function getChannelColorHex(channelKey) {
  const normalized = (channelKey || '').toLowerCase().trim();
  
  const colorMap = {
    'vrbo': '#4A6FAE',
    'airbnb': '#F2BFC0',
    'clc': '#C7E0F4',
    'owner': '#D8CFC4',
  };
  
  return colorMap[normalized] || colorMap['airbnb']; // Default to Airbnb
}
